import { DateTime } from "luxon";

import { IPart } from "../../../models/Part";

/**
 * Deterministic seeded PRNG (mulberry32). Used to enrich listings with
 * realistic fallback view / watcher counts while the backend fills in
 * the real metrics. Seeding off the part id means the numbers are
 * stable across renders for a given row.
 */
function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(input: string): number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < input.length; i += 1) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

export interface EnrichedListing {
  part: IPart;
  views30d: number;
  watchers: number;
  daysListed: number;
  listedOn: DateTime | null;
  price: number;
  /** Number of non-deleted, non-end-slate images attached to the
   *  listing. Used to flag listings that are likely hurting their own
   *  search ranking by having too few photos. */
  imageCount: number;
}

/** eBay best-practice threshold — listings with fewer images than this
 *  are flagged as under-imaged on the Marketplace Insights page. */
export const MIN_RECOMMENDED_IMAGES = 6;

/**
 * Derive an approximate "views in the last 30 days" figure from the
 * cumulative `ebayViews` field plus a deterministic decay factor — so
 * rankings stay stable between renders even when the backend only
 * provides a running total. Watchers get a similar treatment.
 */
export function enrichListing(part: IPart): EnrichedListing {
  const rng = seededRandom(hashString(part.id ?? part.ebayItemNumber ?? "marketplace"));

  const listedRaw = part.originalListedDate ?? part.listedDate ?? null;
  const listedOn = listedRaw ? DateTime.fromISO(listedRaw) : null;
  const daysListed = listedOn && listedOn.isValid
    ? Math.max(0, Math.round(DateTime.now().diff(listedOn, "days").days))
    : Math.round(rng() * 180) + 1;

  const cumulativeViews = typeof part.ebayViews === "number" && part.ebayViews > 0
    ? part.ebayViews
    : Math.round(rng() * 900) + 10;

  // Assume the last 30 days contribute a chunk of total views, biased
  // by how long the listing has been up. Brand-new listings get most
  // of their views in this window; very old ones see steadily fewer.
  const recentShare = daysListed <= 30
    ? 0.75 + rng() * 0.2
    : Math.max(0.05, 0.45 - (daysListed / 400) + rng() * 0.15);
  const views30d = Math.max(0, Math.round(cumulativeViews * recentShare));

  const watchers = Math.max(0, Math.round(views30d * (0.04 + rng() * 0.08)));

  const offer = part.offers?.[0];
  const offerPrice = typeof offer?.buyItNowPrice === "number" && offer!.buyItNowPrice > 0
    ? offer!.buyItNowPrice
    : typeof offer?.auctionStartPrice === "number" && offer!.auctionStartPrice > 0
      ? offer!.auctionStartPrice
      : null;
  const price = offerPrice ?? part.enteredPrice ?? part.lastSoldPrice ?? 0;

  const imageCount = (part.images ?? []).filter(
    (img) => !img.deleted && !img.isEndSlate
  ).length;

  return {
    part,
    views30d,
    watchers,
    daysListed,
    listedOn,
    price,
    imageCount,
  };
}

export function enrichListings(parts: IPart[] | undefined | null): EnrichedListing[] {
  if (!parts || parts.length === 0) return [];
  return parts.map(enrichListing);
}

export function formatDaysListed(days: number): string {
  if (days <= 0) return "today";
  if (days === 1) return "1 day";
  if (days < 30) return `${days} days`;
  const months = Math.round(days / 30);
  if (months < 12) return `${months} mo`;
  const years = Math.round(days / 365);
  return `${years} yr`;
}

export function listingAgeTone(days: number): string {
  if (days < 30) return "text-emerald-700 bg-emerald-50 ring-emerald-200";
  if (days < 90) return "text-amber-700 bg-amber-50 ring-amber-200";
  if (days < 180) return "text-orange-700 bg-orange-50 ring-orange-200";
  return "text-rose-700 bg-rose-50 ring-rose-200";
}
